import { Check, Circle, CircleX, FileText, type LucideIcon, TriangleAlert } from "lucide-react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { PopoverContent, PopoverTitle } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  DOCUMENT_PROCESSING_COPY,
  type DocumentFactRow,
  type DocumentFoundSoFar,
  type DocumentPictureNotice,
  type DocumentPopoverPayload,
  type DocumentProgressStep,
  type DocumentStatusCellTone,
  resolveCompletenessFooterAction,
} from "../lib/document-processing-view";

/**
 * The payload kinds this popover draws.
 *
 * `pre_read` is the one the design gives a *button* rather than a chevron, because it asks a
 * question instead of reporting one; it has its own surface.
 */
export type DocumentStatusPopoverPayload = Extract<
  DocumentPopoverPayload,
  { kind: "reading" | "queued" | "completeness" | "failed" }
>;

export function isDocumentStatusPopoverPayload(
  payload: DocumentPopoverPayload
): payload is DocumentStatusPopoverPayload {
  return (
    payload.kind === "reading" ||
    payload.kind === "queued" ||
    payload.kind === "completeness" ||
    payload.kind === "failed"
  );
}

/** The one action a payload's footer can offer, already bound by the cell. */
export type DocumentStatusPopoverActions = {
  /** Cancel the run — the Stop under a reading or queued document. */
  onStop?: () => void;
  /** Read all the pictures a cap cut out — one press, no question (U12). */
  onReadTheRest?: () => void;
  /**
   * Ask again how much more of a scope-limited document to read (U11).
   *
   * Deliberately separate from `onReadTheRest`: this one opens a panel and the other starts a
   * run, and a caller that bound only one of them offers the wrong thing rather than nothing.
   */
  onReadMore?: () => void;
  /** Re-queue a failure the taxonomy says a retry can still fix. */
  onRetry?: () => void;
  /**
   * Read the whole file again from scratch — the last-resort repair, and the only one that
   * replaces a revision whose stored pictures are gone.
   */
  onReprocess?: () => void;
  /** A press is in flight; the footer button says so rather than looking inert. */
  isPending?: boolean;
  /**
   * Whether another document's row is in the list to be revealed. A row outside the loaded
   * page or behind the active filters has nothing to scroll to, and its name stays words
   * rather than a press that does nothing. Absent, every row is taken to be there.
   */
  canOpenDocument?: (artifactId: string) => boolean;
  /**
   * Open another document's row — the workbook a child came out of, or a child of this one.
   * Absent on a surface with no table to reveal anything in, where the names stay words.
   */
  onOpenDocument?: (artifactId: string) => void;
};

export interface DocumentStatusPopoverProps extends DocumentStatusPopoverActions {
  payload: DocumentStatusPopoverPayload;
  /**
   * The list of files this document carries inside itself, drawn under the read-out.
   *
   * A slot rather than part of the body: it is the one block here with a query behind it, and
   * the panel stays a placer by taking it already rendered. Absent on a surface that does not
   * offer the list — and on every payload that is not a finished read.
   */
  embeddedObjects?: ReactNode;
  /** The file-type glyph the header repeats from the NAME column. */
  icon?: LucideIcon;
  /** Stops a click inside the popover from reaching the row behind it. */
  onSwallowClick?: (event: MouseEvent) => void;
  onSwallowKeyDown?: (event: KeyboardEvent) => void;
}

/**
 * Everything one document can disclose about its own processing, in one panel.
 *
 * The component places strings and nothing else: which sentence a cap deserves, which rows a
 * finished read earns, whether "Try again" is honest — all of it was decided in
 * `lib/document-processing-view.ts` and is testable without a DOM. What lives here is the
 * *shape* the designer drew: a header naming the file, one body per payload kind, and a
 * footer that pairs a timestamp with the single action that state allows.
 */
export function DocumentStatusPopover({
  payload,
  embeddedObjects,
  icon: Icon = FileText,
  onStop,
  onReadTheRest,
  onReadMore,
  onRetry,
  onReprocess,
  isPending = false,
  canOpenDocument,
  onOpenDocument,
  onSwallowClick,
  onSwallowKeyDown,
}: DocumentStatusPopoverProps) {
  const openParent =
    payload.embeddedIn &&
    onOpenDocument &&
    (canOpenDocument?.(payload.embeddedIn.parentArtifactId) ?? true)
      ? onOpenDocument
      : undefined;
  return (
    <PopoverContent
      side="bottom"
      align="end"
      sideOffset={6}
      className="w-90 gap-0 p-0 text-[12px]"
      onClick={onSwallowClick}
      onKeyDown={onSwallowKeyDown}
      data-testid="document-status-popover"
      data-popover-kind={payload.kind}
    >
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
        <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <PopoverTitle className="min-w-0 truncate text-[13px] font-semibold text-foreground">
          {payload.title}
        </PopoverTitle>
      </div>

      <div className="flex flex-col gap-3 px-3.5 py-3">
        {/* A child of another document says so first: everything below reports what was read
            out of these bytes, and this says whose bytes they were. */}
        {payload.embeddedIn ? (
          <p className="text-muted-foreground" data-testid="document-popover-embedded-in">
            {payload.embeddedIn.prefix}
            {openParent ? (
              <button
                type="button"
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                data-testid="document-popover-embedded-parent"
                onClick={() => openParent(payload.embeddedIn?.parentArtifactId ?? "")}
              >
                {payload.embeddedIn.parentName}
              </button>
            ) : (
              payload.embeddedIn.parentName
            )}
            {payload.embeddedIn.suffix}
          </p>
        ) : null}
        {payload.kind === "reading" ? <ReadingBody payload={payload} /> : null}
        {payload.kind === "queued" ? <QueuedBody payload={payload} /> : null}
        {payload.kind === "completeness" ? (
          <CompletenessBody payload={payload} embeddedObjects={embeddedObjects} />
        ) : null}
        {payload.kind === "failed" ? <FailedBody payload={payload} /> : null}
      </div>

      <PopoverFooter
        payload={payload}
        onStop={onStop}
        onReadTheRest={onReadTheRest}
        onReadMore={onReadMore}
        onRetry={onRetry}
        onReprocess={onReprocess}
        isPending={isPending}
      />
    </PopoverContent>
  );
}

/* -------------------------------------------------------------------------- */
/* Bodies                                                                     */
/* -------------------------------------------------------------------------- */

function ReadingBody({
  payload,
}: {
  payload: Extract<DocumentPopoverPayload, { kind: "reading" }>;
}) {
  return (
    <>
      <SectionHeading>{DOCUMENT_PROCESSING_COPY.progressHeading}</SectionHeading>
      <StepList
        steps={payload.steps}
        // The fan-out detail belongs *under* the step it describes, which is the only place
        // "part 7 of 50" and "Reading pages 421–480" read as one fact.
        renderAfterActive={
          payload.partsLine ? (
            <>
              <TrackBar fraction={payload.partsLine.fraction} />
              {payload.partsLine.pages ? (
                <p className="text-muted-foreground tabular-nums">{payload.partsLine.pages}</p>
              ) : null}
            </>
          ) : null
        }
      />
      {payload.foundSoFar ? <FoundSoFar block={payload.foundSoFar} /> : null}
    </>
  );
}

function QueuedBody({ payload }: { payload: Extract<DocumentPopoverPayload, { kind: "queued" }> }) {
  return (
    <>
      <SectionHeading>{DOCUMENT_PROCESSING_COPY.progressHeading}</SectionHeading>
      <StepList
        steps={payload.steps}
        renderAfterActive={
          payload.waitLine ? <p className="text-muted-foreground">{payload.waitLine}</p> : null
        }
      />
    </>
  );
}

function CompletenessBody({
  payload,
  embeddedObjects,
}: {
  payload: Extract<DocumentPopoverPayload, { kind: "completeness" }>;
  embeddedObjects?: ReactNode;
}) {
  return (
    <>
      {/* Above the read-out, not inside it: everything below reports the version on screen,
          and this says why that version is still the one you are looking at. */}
      {payload.alert ? (
        <p className="flex gap-2 text-foreground">
          <TriangleAlert className="mt-px size-3.5 shrink-0 text-warning" aria-hidden />
          <span>{payload.alert}</span>
        </p>
      ) : null}
      <SectionHeading>{DOCUMENT_PROCESSING_COPY.whatWeReadHeading}</SectionHeading>
      <FactRows rows={payload.rows} />
      {/* The note states a decision and the warning reports a problem, so only one of them
          carries the alert glyph — an amber triangle over "as you chose" is the panel telling
          somebody their own answer was a mistake. */}
      {payload.note ? <p className="text-muted-foreground">{payload.note}</p> : null}
      {payload.warning ? (
        <p className="flex gap-2 text-foreground">
          <TriangleAlert className="mt-px size-3.5 shrink-0 text-warning" aria-hidden />
          <span>{payload.warning}</span>
        </p>
      ) : null}
      {/* One sentence per group of pictures this read left out. The glyph is the tone: a loss
          carries the triangle, a fact about the file — a bullet too small to read, a logo that
          repeats — is said in grey, because there is nothing there to repair. */}
      <Notices notices={payload.pictures} />
      {/* Which views of a sheet this read lost, and the one sheet shape that lost nothing. */}
      <Notices notices={payload.renders} />
      {/* The worksheets this read left out, by name. */}
      <Notices notices={payload.sheetsNotRead} />

      {/* The files this document carries, each with the one press it is offered. Below the
          picture notices because it is a list rather than a sentence, and above the footer's
          own offer, which is about the read as a whole. */}
      {embeddedObjects}
      {/* What the footer's one button will do, where that is not obvious from its label. */}
      {payload.readTheRestNote ? (
        <p className="text-muted-foreground">{payload.readTheRestNote}</p>
      ) : null}
      {/* Last, because it is the only line here that is not about the read: it says when the
          file this document was imported from was last looked at. */}
      {payload.sourceCheck ? <p className="text-muted-foreground">{payload.sourceCheck}</p> : null}
    </>
  );
}

function FailedBody({ payload }: { payload: Extract<DocumentPopoverPayload, { kind: "failed" }> }) {
  return (
    <>
      <p className="flex gap-2 text-foreground">
        <CircleX className="mt-px size-3.5 shrink-0 text-destructive" aria-hidden />
        <span>{payload.sentence}</span>
      </p>
      {payload.rows.length > 0 ? (
        <>
          <SectionHeading>{DOCUMENT_PROCESSING_COPY.whatWeKnowHeading}</SectionHeading>
          <FactRows rows={payload.rows} />
        </>
      ) : null}
      {payload.retryNote ? <p className="text-muted-foreground">{payload.retryNote}</p> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Parts                                                                      */
/* -------------------------------------------------------------------------- */

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
      {children}
    </p>
  );
}

/** The PROGRESS list: the same four beats wherever a run's position is worth drawing. */
export function StepList({
  steps,
  renderAfterActive,
  activeIcon,
}: {
  steps: readonly DocumentProgressStep[];
  renderAfterActive?: ReactNode;
  /** What the running step's mark is when it is not running — a paused read shows a pause. */
  activeIcon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {steps.map((step) => (
        <div key={step.label} className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-4">
            <span className="flex min-w-0 items-center gap-2">
              <StepIcon state={step.state} activeIcon={activeIcon} />
              <span
                className={cn(
                  "truncate",
                  step.state === "active"
                    ? "font-semibold text-foreground"
                    : step.state === "done"
                      ? "text-foreground"
                      : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </span>
            {step.detail ? (
              <span
                className={cn(
                  "shrink-0 tabular-nums",
                  step.state === "waiting" ? "text-muted-foreground" : "text-foreground"
                )}
              >
                {step.detail}
              </span>
            ) : null}
          </div>
          {step.state === "active" ? renderAfterActive : null}
        </div>
      ))}
    </div>
  );
}

function StepIcon({
  state,
  activeIcon: ActiveIcon,
}: {
  state: DocumentProgressStep["state"];
  activeIcon?: LucideIcon;
}) {
  if (state === "done") {
    return <Check className="size-3.5 shrink-0 text-foreground" aria-hidden />;
  }
  if (state === "active") {
    // Decorative: the step's own word and its right-hand detail are the accessible status.
    if (ActiveIcon) return <ActiveIcon className="size-3.5 shrink-0 text-primary" aria-hidden />;
    return <Spinner role="presentation" aria-hidden className="size-3.5 shrink-0 text-primary" />;
  }
  return <Circle className="size-3.5 shrink-0 text-muted-foreground/50" aria-hidden />;
}

export function FoundSoFar({ block }: { block: DocumentFoundSoFar }) {
  return (
    <>
      <SectionHeading>{DOCUMENT_PROCESSING_COPY.foundSoFarHeading}</SectionHeading>
      <FactRows rows={block.rows} />
      {block.note ? <p className="text-muted-foreground">{block.note}</p> : null}
    </>
  );
}

function Notices({ notices }: { notices: readonly DocumentPictureNotice[] }) {
  return notices.map((notice) =>
    notice.tone === "warning" ? (
      <p key={notice.text} className="flex gap-2 text-foreground">
        <TriangleAlert className="mt-px size-3.5 shrink-0 text-warning" aria-hidden />
        <span>{notice.text}</span>
      </p>
    ) : (
      <p key={notice.text} className="text-muted-foreground">
        {notice.text}
      </p>
    )
  );
}

function FactRows({ rows }: { rows: readonly DocumentFactRow[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-4">
            <span className="min-w-0 truncate text-muted-foreground">{row.label}</span>
            {/* Wraps rather than overflows: every value here is a short count except the list of
                spans a document read several times holds, and that one is worth a second line. */}
            <span
              className={cn(
                "min-w-0 text-right font-semibold tabular-nums",
                toneTextClass(row.tone)
              )}
            >
              {row.value}
            </span>
          </div>
          {/* Only a count that was cut short of what was found draws a bar, and it draws it
              in the same colour as the number above it. */}
          {row.fraction === undefined ? null : <TrackBar fraction={row.fraction} tone={row.tone} />}
        </div>
      ))}
    </div>
  );
}

function PopoverFooter({
  payload,
  onStop,
  onReadTheRest,
  onReadMore,
  onRetry,
  onReprocess,
  isPending,
}: { payload: DocumentStatusPopoverPayload } & DocumentStatusPopoverActions) {
  const action = resolveFooterAction({
    payload,
    onStop,
    onReadTheRest,
    onReadMore,
    onRetry,
    onReprocess,
  });
  if (!payload.footer && !action) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-3.5 py-2.5">
      <span className="min-w-0 truncate text-muted-foreground">{payload.footer}</span>
      {action ? (
        <Button
          type="button"
          size="sm"
          variant={action.variant}
          disabled={isPending}
          onClick={action.onClick}
          className="shrink-0"
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

type FooterAction = { label: string; variant: "outline" | "default"; onClick: () => void };

/**
 * The single action a state allows, and *which* one — for the `completeness` kind, the
 * precedence is decided in the view-model (`resolveCompletenessFooterAction`), never here; this
 * function only places the label the view-model chose against the callback the caller bound for
 * it. One per popover, deliberately: a panel that offered both "Stop" and "Try again" would be
 * asking the reader to work out which of them their document is actually in a position to
 * take. `Try again` is the only filled button — it is the one press that repairs something.
 */
function resolveFooterAction({
  payload,
  onStop,
  onReadTheRest,
  onReadMore,
  onRetry,
  onReprocess,
}: { payload: DocumentStatusPopoverPayload } & DocumentStatusPopoverActions): FooterAction | null {
  if (payload.kind === "reading" || payload.kind === "queued") {
    return payload.canStop && onStop
      ? { label: DOCUMENT_PROCESSING_COPY.stop, variant: "outline", onClick: onStop }
      : null;
  }
  if (payload.kind === "completeness") {
    const resolved = resolveCompletenessFooterAction(payload);
    if (!resolved) return null;
    const onClick =
      resolved.kind === "try_again"
        ? onRetry
        : resolved.kind === "reprocess"
          ? onReprocess
          : resolved.kind === "read_more"
            ? onReadMore
            : onReadTheRest;
    return onClick ? { label: resolved.label, variant: resolved.variant, onClick } : null;
  }
  return payload.retryOffered && onRetry
    ? { label: DOCUMENT_PROCESSING_COPY.tryAgain, variant: "default", onClick: onRetry }
    : null;
}

/**
 * The thin bar the design uses everywhere a fraction is worth drawing.
 *
 * Not `components/ui/progress`: that primitive owns a 4px track and draws its own bar, and
 * the frames are 3px — a difference that is visible on a table row where the bar sits under
 * a 13px word. Presentational on purpose; the number beside it is what a screen reader
 * reads.
 */
export function TrackBar({
  fraction,
  tone = "default",
}: {
  /** 0–1, or `null` for a run whose position nothing can place. */
  fraction: number | null;
  tone?: DocumentStatusCellTone;
}) {
  const indeterminate = fraction === null;
  return (
    <span aria-hidden className="flex h-[3px] w-full overflow-hidden bg-muted">
      <span
        className={cn(
          "h-full transition-all",
          toneBarClass(tone),
          // Nothing can place this run, so the bar says "moving" rather than inventing a
          // position it does not have.
          indeterminate && "w-1/3 animate-pulse"
        )}
        style={indeterminate ? undefined : { width: `${Math.round(fraction * 100)}%` }}
      />
    </span>
  );
}

export function toneTextClass(tone: DocumentStatusCellTone): string {
  if (tone === "destructive") return "text-destructive";
  if (tone === "warning") return "text-warning";
  // A fact that is true because somebody chose it reads in the same grey as the label beside
  // it: stated, not flagged.
  return tone === "muted" ? "text-muted-foreground" : "text-foreground";
}

function toneBarClass(tone: DocumentStatusCellTone): string {
  if (tone === "destructive") return "bg-destructive";
  if (tone === "warning") return "bg-warning";
  return tone === "muted" ? "bg-muted-foreground" : "bg-primary";
}
